import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import BoxButton from '@components/Buttons/BoxButton';
import CheckboxField from '@components/Fields/CheckboxField';
import { useCallback, useState } from 'react';
import loadable from '@loadable/component';
import CrossCloseIcon from '@components/UI/CrossCloseIcon';
import Dots from '@components/UI/Dots';
import ContentCarousel from '@components/UI/ContentCarousel';
import SectionFooter from '@components/UI/Section/components/SectionFooter';
import ModalBody from '@components/Modals/components/ModalBody';
import ModalContent from '@components/Modals/components/ModalContent';
import { styled } from '@mui/system';
import postSeenTour from '@domain/Tournament/api/postSeenTour';
import { useUpdateUserState, useUserState } from '@context/UserContext';
import { useTheme, useMediaQuery } from '@mui/material';
import GenericModal from './GenericModal';
import ModalImageHeader from './components/ModalImageHeader';
import ModalTextContent from './components/ModalTextContent';
const CircleArrowRight = loadable(() => import('@components/UI/Icon/Icons/CircleArrowRight'));
const Icon = loadable(() => import('@components/Icons/Icon'));
const CloseIcon = styled(CrossCloseIcon) `
  margin-left: auto;
`;
const CardInfo = styled('div') `
  align-items: center;
  background: linear-gradient(180deg, #f3f8fe 0%, rgba(243, 248, 254, 0) 100%);
  border: 1px solid ${({ theme }) => theme.palette.grey[100]};
  border-radius: 8px;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(4)};
  row-gap: ${({ theme }) => theme.spacing(4)};
  text-align: center;

  * {
    margin: 0 !important;
  }

  > div {
    align-items: center;
    display: flex;
    flex-basis: 100px;
    justify-content: space-between;
  }

  img {
    width: 100%;
    max-width: 180px;
  }
`;
const CardContainer = styled('div') `
  display: flex;
  column-gap: 12px;
  max-width: 100%;
  padding-bottom: 24px;
`;
const ActionContainer = styled('div') `
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing(1, 0)};
  row-gap: ${({ theme }) => theme.spacing(2.5)};
`;
const StyledCheckboxField = styled(CheckboxField) `
  color: ${({ theme }) => theme.palette.grey[500]}!important;
  display: block;
  font-weight: normal;
  margin: 0;
  text-align: center;

  input[type='checkbox'] {
    height: unset;
    line-height: unset;
  }
`;
const ModalHeaderImage = styled(ModalImageHeader) `
  img {
    width: 100%;
    height: 100%;
  }
  background: transparent;
  i {
    color: #62686d;
  }
`;
const SlideNavigation = styled('div') `
  position: absolute;
  display: flex;
  align-items: center;
  height: 233px;
  width: 100%;
`;
const CarouselNavigation = styled('div') `
  height: 233px;
  align-items: center;
  display: flex;
  cursor: pointer;
`;
const SlideNavigationLeft = styled(CarouselNavigation) `
  justify-content: left;
`;
const SlideNavigationRight = styled(CarouselNavigation) `
  justify-content: right;
`;
const CarouselArrow = styled(Icon) `
  position: absolute;
  height: 54px;
  padding: ${(props) => props.theme.spacing(2)};
  z-index: 1;
  fill: #25282b;
`;
const LeftSlide = styled(CarouselArrow) `
  left: 0;
`;
const RightSlide = styled(CarouselArrow) `
  right: 0;
`;
const DotsFooter = styled(SectionFooter) `
  background: #fff;
  border: none;
  height: 70px;
`;
const MobileFooter = styled('div') `
  padding: 0 20px 20px;
  display: flex;
  flex-direction: column;
`;
const HowToParticipateModal = ({ onClose }) => {
    const [dontShowModal, setDontShowModal] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const userStateUpdater = useUpdateUserState();
    const { userDetails } = useUserState();
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const setCarouselIndex = (idx) => {
        setCurrentStepIndex(idx);
    };
    const onContinue = useCallback(() => {
        onClose();
        if (dontShowModal) {
            void postSeenTour('how-to-participate');
            userStateUpdater.setUserDetailsCache({
                tours: [...((userDetails === null || userDetails === void 0 ? void 0 : userDetails.tours) || []), `tours.seen.how-to-participate`],
            });
        }
    }, [dontShowModal]);
    if (isMobile) {
        return (_jsx(GenericModal, { mobileBottomSheet: true, onClose: onClose, onExited: onClose, size: "extraLarge", show: true, children: _jsxs(ModalBody, { children: [_jsxs(SlideNavigation, { children: [_jsx(SlideNavigationLeft, { onClick: () => setCarouselIndex(currentStepIndex !== 0 ? currentStepIndex - 1 : 2), "aria-hidden": "true", children: _jsx(LeftSlide, { icon: "chevronleft", title: "Previous Slide" }) }), _jsx(SlideNavigationRight, { onClick: () => setCarouselIndex(currentStepIndex !== 2 ? currentStepIndex + 1 : 0), "aria-hidden": "true", children: _jsx(RightSlide, { icon: "chevronright", title: "Next Slide" }) })] }), _jsxs(ContentCarousel, { name: "how-to-participate", currentStep: currentStepIndex, children: [_jsxs(_Fragment, { children: [_jsx(ModalHeaderImage, { imageUrl: "/img/how-to-play/play-game-mobile.png", onClose: onClose }), _jsx(ModalTextContent, { title: "Play Matches", paragraph: "Play matches as usual and we\u2019ll automatically track your scores" })] }), _jsxs(_Fragment, { children: [_jsx(ModalHeaderImage, { imageUrl: "/img/how-to-play/get-qualified-mobile.png", onClose: onClose }), _jsx(ModalTextContent, { title: "Get Qualified", paragraph: "Play at least the minimum amount of games to qualify" })] }), _jsxs(_Fragment, { children: [_jsx(ModalHeaderImage, { imageUrl: "/img/how-to-play/win-rewards-mobile.png", onClose: onClose }), _jsx(ModalTextContent, { title: "Win Rewards", paragraph: "When the tournament ends you\u2019ll get rewarded based on your rank, playing more increases the chance at a higher rank" })] })] }), _jsxs(MobileFooter, { children: [_jsxs(BoxButton, { onClick: onContinue, children: ["Continue ", _jsx(CircleArrowRight, {})] }), _jsx(DotsFooter, { children: _jsx(Dots, { onChange: setCarouselIndex, value: currentStepIndex, slideCount: 3, slidesPerPage: 1 }) }), _jsx(StyledCheckboxField, { label: "Don't show this again", onChange: (e) => setDontShowModal(e.currentTarget.checked), value: dontShowModal, name: "dontShowParticipateModal", checked: dontShowModal })] })] }) }));
    }
    return (_jsxs(GenericModal, { onClose: onClose, onExited: onClose, size: "extraLarge", show: true, children: [_jsx(CloseIcon, { action: onClose }), _jsx(ModalBody, { children: _jsxs(ModalContent, { children: [_jsxs(CardContainer, { children: [_jsxs(CardInfo, { children: [_jsx("h2", { children: "Play Matches" }), _jsx("img", { src: "/img/how-to-play/play-game.png" }), _jsx("div", { children: _jsx("p", { children: "Play matches as usual and we\u2019ll automatically track your scores" }) })] }), _jsxs(CardInfo, { children: [_jsx("h2", { children: "Get Qualified" }), _jsx("img", { src: "/img/how-to-play/get-qualified.png" }), _jsx("div", { children: _jsx("p", { children: "Play at least the minimum amount of games to qualify" }) })] }), _jsxs(CardInfo, { children: [_jsx("h2", { children: "Win Rewards" }), _jsx("img", { src: "/img/how-to-play/win-rewards.png" }), _jsx("div", { children: _jsx("p", { children: "When the tournament ends you\u2019ll get rewarded based on your rank, playing more increases the chance at a higher rank" }) })] })] }), _jsxs(ActionContainer, { children: [_jsxs(BoxButton, { onClick: onContinue, children: ["Continue to Tournament ", _jsx(CircleArrowRight, {})] }), _jsx(StyledCheckboxField, { label: "Don't show this again", onChange: (e) => setDontShowModal(e.currentTarget.checked), value: dontShowModal, name: "dontShowParticipateModal", checked: dontShowModal })] })] }) })] }));
};
export default HowToParticipateModal;
